import { string, z } from "zod";
import { zStringDate, zStringId } from "../../utils";
import {
  ProfileExperienceLevelEnum,
  ProfileLanguageProficiency,
  ProfileTemplatesEnum,
  ProfileTypesEnum,
} from "./profile-statics";
import { zUserSnippet } from "../users";
import { zLocationSnippet } from "../users/user-resource";

const zProfileCommonFields = {
  _id: zStringId(),
  userDoc: zUserSnippet,
  selectedProfileTemplate: z.nativeEnum(ProfileTemplatesEnum).optional(),

  primaryLocation: zLocationSnippet.optional(),
  languages: z
    .object({
      languageName: z.string(),
      proficiency: z.nativeEnum(ProfileLanguageProficiency),
    })
    .array(),
};

const zProfileCompanyInfoFields = {
  legalName: z.string(),
  dba: z.string().optional(),
  about: z.string(),
};

const zProfileCompanyOverviewFields = {
  companyType: z.string(),
  numberOfEmployees: z.number(),
};

const zProfileCommonProviderFields = {
  experienceLevel: z.nativeEnum(ProfileExperienceLevelEnum).optional(),
  linkedAccounts: z
    .object({
      accountOn: z.string(),
      profileLink: z.string(),
    })
    .array(),
  profileCategory: z
    .object({
      categoryDoc: z.object({
        _id: zStringId(),
        name: string(),
      }),
      subCategoryDoc: z.object({
        _id: zStringId(),
        name: string(),
      }),
      skillsIds: z.string().array(),
    })
    .optional(),
};

export const zPublicProfileOfClient = z.object({
  profileType: z.literal(ProfileTypesEnum.CLIENT),
  ...zProfileCommonFields,
});

export const zPublicProfileOfCompany = z.object({
  profileType: z.literal(ProfileTypesEnum.COMPANY),
  ...zProfileCommonFields,
  companyInfo: z.object({ ...zProfileCompanyInfoFields }).optional(),
  companyOverview: z
    .object({
      ...zProfileCompanyOverviewFields,
      representativeTitle: z.string().optional(),
    })
    .optional(),
});

export const zPublicProfileOfProfessional = z.object({
  profileType: z.literal(ProfileTypesEnum.PROFESSIONAL),
  ...zProfileCommonFields,
  ...zProfileCommonProviderFields,
  title: z.string().optional(),
  overview: z.string().optional(),
  workHistory: z
    .object({
      company: z.string(),
      jobTitle: z.string(),
      startAt: zStringDate(),
      endAt: zStringDate().optional(),
      isWorking: z.boolean(),
      description: z.string().optional(),
      location: z
        .object({
          city: z.string(),
          country: z.string(),
        })
        .optional(),
    })
    .array(),
  educationHistory: z
    .object({
      college: z.string(),
      area: z.string().optional(),
      degree: z.string().optional(),
      start: zStringDate().optional(),
      end: zStringDate().optional(),
      description: z.string().optional(),
    })
    .array(),
});

export const zPublicProfileOfBusiness = z.object({
  profileType: z.literal(ProfileTypesEnum.BUSINESS),
  ...zProfileCommonFields,
  ...zProfileCommonProviderFields,
  trustedBy: z
    .object({
      name: z.string(),
    })
    .array(),

  companyInfo: z
    .object({
      ...zProfileCompanyInfoFields,
      licenseNumber: z.string(),
    })
    .optional(),

  companyOverview: z
    .object({
      ...zProfileCompanyOverviewFields,
      industryName: z.string(),
      establishedYear: zStringDate(),
    })
    .optional(),

  qualifications: z
    .object({
      title: z.string(),
      expiresAt: zStringDate(),
      certificateLink: z.string(),
    })
    .array(),
});

export const zPublicProfile = z.union([
  zPublicProfileOfClient,
  zPublicProfileOfCompany,
  zPublicProfileOfProfessional,
  zPublicProfileOfBusiness,
]);

export type PublicProfile = z.infer<typeof zPublicProfile>;
export type PublicProfileOfClient = z.infer<typeof zPublicProfileOfClient>;
export type PublicProfileOfCompany = z.infer<typeof zPublicProfileOfCompany>;
export type PublicProfileOfProfessional = z.infer<typeof zPublicProfileOfProfessional>;
export type PublicProfileOfBusiness = z.infer<typeof zPublicProfileOfBusiness>;
